import feltColors from '~/server/utils/threeDDesignSync/felt-colors.json'

/** Catalog configuration and saved selection for two-sheet engraved tiles. */
export interface LayeredEngravingCatalog {
  pattern: string
  top_color: string
  tiles_per_set: number
  depth_mm: number
  default_bottom_color: string
  default_bottom_hex: string
}
export interface LayeredEngravingSelection {
  top_color: string
  top_hex: string
  bottom_color: string
  bottom_hex: string
  overlay_url: string
  tiles_per_set: number
  depth_mm: number
}
export const isLayeredShape = (shape: any): boolean =>
  shape?.finishFamily === 'layered-engraved' || /^LayeredEngraved/.test(shape?.id || '')
export const layeredSelection = (shape: any): LayeredEngravingSelection | undefined =>
  (shape?.metadata || shape?.attrs?.metadata)?.layered_engraving
export const tileConfigurationKey = (metadata: any): string => {
  const layer = metadata?.layered_engraving
  return layer ? `${metadata.variant_id}:${encodeURIComponent(layer.bottom_color)}` : metadata?.variant_id
}

/** Compare color names ignoring whitespace and case (e.g. 'Hot Pink' vs 'HotPink'). */
export function isSameColor(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false
  if (a === b) return true
  return a.replace(/\s+/g, '').toLowerCase() === b.replace(/\s+/g, '').toLowerCase()
}

/** Look up felt color hex code, normalizing spaces and case. */
export function getFeltColorHex(label?: string | null): string | undefined {
  if (!label) return undefined
  const direct = (feltColors as Record<string, string>)[label]
  if (direct) return direct
  const clean = label.replace(/\s+/g, '').toLowerCase()
  for (const [name, hex] of Object.entries(feltColors as Record<string, string>)) {
    if (name.replace(/\s+/g, '').toLowerCase() === clean) return hex
  }
  return undefined
}
/** Reject partial/mixed sets before pricing or writing a cart snapshot. */
export function validateLayeredSets(shapes: any[]): void {
  const groups = new Map<string, any[]>()
  for (const shape of shapes) {
    const layer = layeredSelection(shape)
    if (!layer) {
      if (/^LayeredEngraved/.test((shape.metadata || shape.attrs?.metadata)?.shape_id || '')) throw new Error('Layered engraved selection is missing its colors.')
      continue
    }
    const meta = shape.metadata || shape.attrs?.metadata
    if (!meta.set_id || layer.tiles_per_set !== 8 || layer.depth_mm !== 18 ||
        !layer.top_color || !/^#[\da-f]{6}$/i.test(layer.top_hex) || !layer.bottom_color || !/^#[\da-f]{6}$/i.test(layer.bottom_hex)) {
      throw new Error('Layered engraved tiles require a complete 18 mm set and two valid colors.')
    }
    const group = groups.get(meta.set_id) || []
    group.push(shape)
    groups.set(meta.set_id, group)
  }
  for (const group of groups.values()) {
    const first = group[0].metadata || group[0].attrs.metadata
    if (group.length !== 8 || group.some(shape => {
      const meta = shape.metadata || shape.attrs.metadata
      return tileConfigurationKey(meta) !== tileConfigurationKey(first) ||
        meta.product_id !== first.product_id || JSON.stringify(meta.layered_engraving) !== JSON.stringify(first.layered_engraving)
    })) throw new Error('Each layered engraved set must contain eight tiles with the same top and bottom colors.')
  }
}
export function layeredCartSummary(tiles: Record<string, any>): string {
  return Object.values(tiles).filter(tile => tile.layered_engraving).map(tile =>
    `${tile.quantity} × set of 8 · ${tile.shape_label || 'Layered engraved'} · Top: ${tile.layered_engraving.top_color} · Bottom: ${tile.layered_engraving.bottom_color}`,
  ).join('; ')
}
